// @flow strict
import React from 'react';
import { useSiteMetadata } from '../../hooks';
import { getIcon } from '../../utils';
import Icon from '../Icon';
import styles from './WhatsAppWidget.module.scss';

const PROMPT_MESSAGE =
  "Happy to have you here :) Send a message if you're exploring Montessori, positive discipline, or gentle parenting and want a simple place to start.";
const WHATSAPP_MESSAGE = '';

type Props = {
  showPrompt?: boolean,
};

const WhatsAppWidget = ({ showPrompt = false }: Props) => {
  const { author } = useSiteMetadata();
  const whatsapp = author && author.contacts ? author.contacts.whatsapp : '';
  const whatsappNumber = whatsapp ? whatsapp.replace(/[^\d]/g, '') : '';

  if (!whatsappNumber) {
    return null;
  }

  const whatsappHref = WHATSAPP_MESSAGE
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
        WHATSAPP_MESSAGE
      )}`
    : `https://wa.me/${whatsappNumber}`;
  const [isOpen, setIsOpen] = React.useState(false);
  const [isDismissed, setIsDismissed] = React.useState(false);
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const [typedText, setTypedText] = React.useState('');
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  React.useEffect(() => {
    if (!showPrompt) {
      return undefined;
    }

    if (isDismissed) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 2200);

    return () => clearTimeout(timer);
  }, [isDismissed]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updatePreference);
      return () => mediaQuery.removeEventListener('change', updatePreference);
    }

    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);

  React.useEffect(() => {
    if (!showPrompt || !isOpen || isChatOpen) {
      return undefined;
    }

    if (prefersReducedMotion) {
      setTypedText(PROMPT_MESSAGE);
      return undefined;
    }

    setTypedText('');
    let index = 0;
    const timer = setInterval(() => {
      index += 1;
      setTypedText(PROMPT_MESSAGE.slice(0, index));
      if (index >= PROMPT_MESSAGE.length) {
        clearInterval(timer);
      }
    }, 34);

    return () => clearInterval(timer);
  }, [isOpen, isChatOpen, prefersReducedMotion]);

  const isTyping = typedText.length < PROMPT_MESSAGE.length;

  const shouldShowPrompt = showPrompt && isOpen && !isChatOpen;
  const shouldShowChat = showPrompt && isChatOpen;

  return (
    <div className={styles['widget']}>
      <div
        className={`${styles['bubble']} ${
          shouldShowPrompt ? '' : styles['bubble--hidden']
        }`}
      >
        <button
          type="button"
          className={styles['bubble__close']}
          onClick={() => {
            setIsOpen(false);
            setIsDismissed(true);
          }}
          aria-label="Close WhatsApp prompt"
        >
          x
        </button>
        <p className={styles['bubble__title']}>
          Chat with Practical Montessori
        </p>
        <button
          type="button"
          className={styles['bubble__message']}
          onClick={() => setIsChatOpen(true)}
          aria-label="Open chat window"
        >
          <span className={styles['bubble__text']}>{typedText}</span>
          <span
            className={`${styles['bubble__caret']} ${
              isTyping && !prefersReducedMotion
                ? ''
                : styles['bubble__caret--hidden']
            }`}
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          className={styles['bubble__link']}
          onClick={() => setIsChatOpen(true)}
        >
          Open chat
        </button>
      </div>
      <div
        className={`${styles['chat']} ${
          shouldShowChat ? '' : styles['chat--hidden']
        }`}
      >
        <div className={styles['chat__header']}>
          <div className={styles['chat__title']}>
            <span className={styles['chat__icon']}>
              <Icon name="whatsapp" icon={getIcon('whatsapp')} />
            </span>
            <div>
              <p className={styles['chat__name']}>WhatsApp</p>
              <p className={styles['chat__status']}>
                Typically replies within a few hours
              </p>
            </div>
          </div>
          <button
            type="button"
            className={styles['chat__close']}
            onClick={() => {
              setIsChatOpen(false);
              setIsOpen(false);
              setIsDismissed(true);
            }}
            aria-label="Close chat window"
          >
            x
          </button>
        </div>
        <div className={styles['chat__body']}>
          <div className={styles['chat__bubble']}>
            <p className={styles['chat__text']}>{PROMPT_MESSAGE}</p>
            <span className={styles['chat__time']}>Just now</span>
          </div>
        </div>
        <div className={styles['chat__footer']}>
          <a
            className={styles['chat__cta']}
            href={whatsappHref}
            rel="noopener noreferrer"
            target="_blank"
          >
            START CHAT
          </a>
        </div>
      </div>
      <a
        className={styles['button']}
        href={whatsappHref}
        rel="noopener noreferrer"
        target="_blank"
        aria-label="Chat on WhatsApp"
      >
        <span className={styles['button__icon']}>
          <Icon name="whatsapp" icon={getIcon('whatsapp')} />
        </span>
      </a>
    </div>
  );
};

export default WhatsAppWidget;
